import {IModalStore} from "../global/modalStore";
import {IResidentAdminDomainStore} from "../../domainStore/pages/residentAdminDomainStore";
import {action, makeObservable, observable, runInAction} from "mobx";
import {ObservableList} from "../../observables/observableList";
import {KeyValue} from "../../observables/keyValue";
import {AppAccessStatus} from "../../../shared/enums/appAccessStatus";
import {SortOrder} from "../../../shared/enums/sortOrder";
import {IAccountStore} from "../global/accountStore";

/**
 * The interface for load residents for admin UI store.
 */
export interface IResidentAdminUiStore {
  /**
   * Indicating that the resident admin UI is navigating.
   */
  isNavigating: boolean;
  /**
   * Indicating that the resident admin list is loading.
   */
  isListLoading: boolean;
  /**
   * Indicating that the resident admin is in the create or edit state.
   */
  isCreatingOrEditing: boolean;
  /**
   * The event handler when the village has changed.
   */
  onSelectedVillageChange: () => Promise<void>;
  /**
   * The list of selectable villages
   */
  selectableVillages?: ObservableList<KeyValue>;

  refreshResidentData: (persistFilters?: boolean) => Promise<void>;

  /**
   * Reset the data back to it's default state
   */
  resetResidentData: () => void;

  setSearchTerm: (searchTerm: string) => void;

  setStatusFilter: (searchStatus?: AppAccessStatus) => void;

  currentResidentAdminListSearchTerm: string | undefined;

  /**
   * The status filter for the resident admin list.
   */
  statusFilter: AppAccessStatus | undefined;

  setCurrentSortField: (sortField: string) => void;

  currentSortOrder: SortOrder | undefined;

  setCurrentSortOrder: (sortOrder: SortOrder) => void;

  currentSortField: string | undefined;
}

/**
 * The UI store for residents.
 */
export class ResidentAdminUiStore implements IResidentAdminUiStore {
  private residentAdminDomainStore: IResidentAdminDomainStore;
  private modalStore: IModalStore;
  private accountStore: IAccountStore;
  private refreshSequence: number = 0;
  isNavigating: boolean;
  isListLoading: boolean;
  isCreatingOrEditing: boolean = false;
  currentResidentAdminListSearchTerm: string | undefined;
  selectableVillages: ObservableList<KeyValue>;
  statusFilter: AppAccessStatus | undefined;
  currentSortField: string | undefined;
  currentSortOrder: SortOrder | undefined;

  constructor(
    residentAdminDomainStore: IResidentAdminDomainStore,
    modalStore: IModalStore,
    selectableVillages: ObservableList<KeyValue>,
    accountStore: IAccountStore
  ) {
    makeObservable(this, {
      isNavigating: observable,
      isListLoading: observable,
      isCreatingOrEditing: observable,
      resetResidentData: action,
      currentResidentAdminListSearchTerm: observable,
      statusFilter: observable,
      currentSortOrder: observable,
      currentSortField: observable
    });
    this.isNavigating = false;
    this.isListLoading = false;
    this.residentAdminDomainStore = residentAdminDomainStore;
    this.modalStore = modalStore;
    this.currentResidentAdminListSearchTerm = undefined;
    this.selectableVillages = selectableVillages;
    this.accountStore = accountStore;
  }

  onSelectedVillageChange = async () => {
    if (
      this.selectableVillages.selectedItem?.value &&
      this.accountStore.abilities.can("Update", "ResidentAdmin")
    ) {
      // If the village has changed, we should load from page 1 again.
      // refreshResidentData owns the isListLoading lifecycle.
      await this.refreshResidentData();
    }
  };

  refreshResidentData = async (persistFilters?: boolean) => {
    // When updating observables in async, we need to mark them as action. Otherwise, warnings will be shown in the browser's developer console.
    // https://mobx.js.org/actions.html#asynchronous-actions

    // An invalidated refresh can resolve while a newer one is still fetching
    // (e.g. village switched mid-load); only the latest may clear the flags.
    const refreshId = ++this.refreshSequence;

    runInAction(() => {
      this.isListLoading = true;

      if (!persistFilters) {
        this.statusFilter = undefined;
      }
    });

    try {
      await this.residentAdminDomainStore.loadVillageResidents();
    } catch (error) {
      if (error instanceof Error) {
        this.modalStore.openErrorModal(error as Error);
      } else {
        this.modalStore.openCustomErrorModal("An unspecified error occurred.");
      }
    } finally {
      if (refreshId === this.refreshSequence) {
        runInAction(() => {
          this.isListLoading = false;
          this.isNavigating = false;
        });
      }
    }
  };

  setSearchTerm = (searchTerm: string) => {
    runInAction(() => {
      this.currentResidentAdminListSearchTerm = searchTerm;
    });
  };

  setStatusFilter = (searchStatus?: AppAccessStatus) => {
    runInAction(() => {
      this.statusFilter = searchStatus;
    });

    runInAction(() => {
      this.currentResidentAdminListSearchTerm = undefined;
    });
  };

  setCurrentSortField = (sortField: string) => {
    runInAction(() => {
      this.currentSortField = sortField;
    });
  };

  setCurrentSortOrder = (sortOrder: SortOrder) => {
    runInAction(() => {
      this.currentSortOrder = sortOrder;
    });
  };

  resetResidentData = () => {
    runInAction(() => {
      this.isNavigating = false;
      this.isListLoading = false;
      this.currentResidentAdminListSearchTerm = undefined;
    });

    this.residentAdminDomainStore.resetResidents();
  };
}
